import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ShareOutline, CopyOutline, PersonAddOutline, ColorWandOutline } from "react-ionicons";
import { Users, Gift, Flame, Trophy, Star, ArrowRight, Zap, Target } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { appName, appUrl, mainCryptoName } from "../../../constants";
import { THEME_COLORS } from "../../themes/DarkLight";
import useThemeHook from "../../hooks/useThemeHook";
import { useTranslationHook } from "../../hooks/useTranslationHook";
import Text from "../../components/ThemedText";
import emitter, { EmitterEvents } from "../../utils/eventsEmitter";
import ReferralMilestones from "../../components/ReferralMilestones";
import arthereumProvider from "../../../backend/ArthereumProvider";
import { User } from "../../../types";

const InviteUsersPage = () => {
  const userData = useSelector((state) => (state as any)?.actualUser);

  const getTranslationText = useTranslationHook();
  const theme = useThemeHook();
  const navigate = useNavigate();
  
  const [topInviters, setTopInviters] = useState<User[]>([]);
  const [recentActivity, setRecentActivity] = useState<string[]>([]);
  const [isWeekend, setIsWeekend] = useState(false);

  useEffect(() => {
    const fetchTopInviters = async () => {
      const top = await arthereumProvider.getTopInviters(3);
      setTopInviters(top);
    };
    
    fetchTopInviters();

    // Check if it's weekend
    const day = new Date().getDay();
    setIsWeekend(day === 0 || day === 6);

    // Simulate recent activity
    const usernames = ["cryptoking", "pi_warrior", "ath_miner", "rich_adams", "blockchain_boss", "moon_shot", "web3_wiz", "alpha_user"];
    const generateActivity = () => {
      const user = usernames[Math.floor(Math.random() * usernames.length)];
      setRecentActivity(prev => [`@${user} just invited a friend! 🚀`, ...prev].slice(0, 3));
    };

    generateActivity();
    const interval = setInterval(generateActivity, 8000);
    return () => clearInterval(interval);
  }, []);

  const copyInviteLink = async () => {
    try {
      const shareUrl = `${appUrl}/invite/${userData?.user?.username}`;
      await navigator.clipboard.writeText(shareUrl);
      
      // Track share for daily quests
      if (userData?.user?.id) {
        arthereumProvider.incrementDailyStat(userData.user.id, "sharesCount");
      }
      
      alert(getTranslationText("wallet_address_copied"));
    } catch (error) {
      console.error("Error copy link:", error);
    }
  };

  const shareInvite = async () => {
    try {
      const shareUrl = `${appUrl}/invite/${userData?.user?.username}`;
      const shareText = getTranslationText("join_arthereum_wallet", {
        appName,
        mainCryptoName,
      });

      if (navigator.share) {
        await navigator.share({
          title: getTranslationText("invite_friends_and_family"),
          text: shareText,
          url: shareUrl,
        });
      } else {
        await copyInviteLink();
      }
    } catch (error) {
      console.error("Error share:", error);
    }
  };

  return (
    <div className={`min-h-screen ${theme === "dark" ? "bg-gray-900" : "bg-gray-50"}`}>
      {/* Social Proof Ticker */}
      <div className={`py-2 px-4 shadow-sm relative z-20 ${theme === "dark" ? "bg-blue-600/10" : "bg-blue-50"}`}>
        <div className="flex items-center gap-2 overflow-hidden h-6">
          <Flame size={14} className="text-orange-500 flex-shrink-0" />
          <AnimatePresence mode="wait">
            <motion.p
              key={recentActivity[0]}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              className={`text-[11px] font-bold ${theme === "dark" ? "text-blue-300" : "text-blue-700"}`}
            >
              {recentActivity[0]}
            </motion.p>
          </AnimatePresence>
        </div>
      </div>

      {/* Header with Background Gradient */}
      <div className="relative pt-8 pb-8 px-6 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-blue-600/20 to-transparent pointer-events-none"></div>
        <div className="absolute top-10 right-10 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        
        <div className="relative flex flex-col items-center">
          <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-600/20 mb-6 transform rotate-3 relative">
            <PersonAddOutline color="white" height="32px" width="32px" />
            {isWeekend && (
              <div className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full shadow-lg border-2 border-white animate-bounce">
                2X
              </div>
            )}
          </div>
          <h1 className={`text-2xl md:text-3xl font-bold text-center mb-2 ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
            {getTranslationText("invite_friends_and_family")}
          </h1>
          <p className={`text-center max-w-sm px-4 ${theme === "dark" ? "text-gray-400" : "text-gray-500"} text-sm leading-relaxed`}>
            Build your squad and earn passive ATH rewards from every referral.
          </p>
        </div>
      </div>

      <div className="max-w-md mx-auto px-6 space-y-6 pb-20">
        <ReferralMilestones referralCount={userData?.user?.referalsCount || 0} />
        
        {/* Weekend Booster Banner */}
        {isWeekend && (
          <div className="bg-gradient-to-r from-red-600 to-orange-500 rounded-2xl p-4 text-white shadow-lg overflow-hidden relative">
            <div className="absolute -right-4 -top-4 opacity-20">
              <Zap size={80} fill="white" />
            </div>
            <div className="flex items-center gap-3 relative z-10">
              <div className="p-2 bg-white/20 rounded-xl">
                <Flame size={20} />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-tighter opacity-80">Weekend Special Reward</p>
                <h3 className="text-lg font-black leading-tight">400 ATH bonus per friend!</h3>
              </div>
            </div>
          </div>
        )}
        
        {/* Earnings Card */}
        <div className={`relative p-6 rounded-3xl border border-white/10 backdrop-blur-md overflow-hidden shadow-xl ${
          theme === "dark" ? "bg-gray-800/50" : "bg-white"
        }`}>
          <div className="absolute top-0 right-0 p-4">
            <ColorWandOutline color={THEME_COLORS[theme].gold} height="20px" width="20px" />
          </div>
          
          <p className={`text-xs font-semibold uppercase tracking-wider mb-1 ${theme === "dark" ? "text-blue-400" : "text-blue-600"}`}>
            Massive Rewards
          </p>
          <div className="flex flex-col gap-1 mb-4">
            <div className="flex items-baseline gap-2">
              <span className={`text-4xl font-black ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                {isWeekend ? "400" : "200"} ATH
              </span>
              <span className={`text-sm ${theme === "dark" ? "text-gray-500" : "text-gray-400"}`}>per friend</span>
            </div>
            <div className="flex items-center gap-1.5 mt-1 bg-amber-500/10 text-amber-500 px-3 py-1 rounded-xl w-fit border border-amber-500/20">
              <Gift size={14} className="animate-bounce text-amber-500" />
              <span className="text-xs font-black uppercase tracking-wider">
                + 5 Pi Coin Bounty! ❄️
              </span>
            </div>
          </div>
          
          <div className={`h-px w-full mb-4 ${theme === "dark" ? "bg-gray-700" : "bg-gray-100"}`}></div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className={`text-[10px] uppercase mb-1 ${theme === "dark" ? "text-gray-500" : "text-gray-400"}`}>Active Referrals</p>
              <p className={`text-xl font-bold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>{userData?.user?.referalsCount || 0}</p>
            </div>
            <div>
              <p className={`text-[10px] uppercase mb-1 ${theme === "dark" ? "text-gray-500" : "text-gray-400"}`}>Squad Commission</p>
              <div className="flex items-center gap-1">
                <span className="text-xl font-bold text-green-500">10%</span>
                <span className="text-[10px] text-gray-500 font-medium">+ 5% Tier 2</span>
              </div>
            </div>
          </div>
        </div>

        {/* Top Inviters Mini Leaderboard */}
        {topInviters.length > 0 && (
          <div className={`p-4 rounded-3xl border ${theme === "dark" ? "bg-gray-800/30 border-gray-700" : "bg-white border-gray-200 shadow-sm"}`}>
            <div className="flex items-center justify-between mb-4 px-1">
              <h4 className={`text-sm font-bold flex items-center gap-2 ${theme === "dark" ? "text-gray-200" : "text-gray-900"}`}>
                <Trophy size={16} className="text-yellow-500" /> Top Inviters
              </h4>
              <span className="text-[10px] uppercase font-bold text-gray-400">This Week</span>
            </div>
            <div className="space-y-3">
              {topInviters.map((user, idx) => (
                <div key={user.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                      idx === 0 ? "bg-yellow-500 text-white" : 
                      idx === 1 ? "bg-slate-400 text-white" : 
                      "bg-orange-500 text-white"
                    }`}>
                      {idx + 1}
                    </div>
                    <span className={`text-xs font-semibold ${theme === "dark" ? "text-gray-300" : "text-gray-700"}`}>
                      @{user.username}
                    </span>
                  </div>
                  <span className={`text-xs font-black ${theme === "dark" ? "text-blue-400" : "text-blue-600"}`}>
                    {user.referalsCount} Friends
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Viral Hooks Map */}
        <div className="grid grid-cols-2 gap-3">
          <div className={`p-4 rounded-2xl ${theme === "dark" ? "bg-gray-800" : "bg-white"} border ${theme === "dark" ? "border-gray-700" : "border-gray-100"}`}>
            <Target className="w-6 h-6 text-purple-500 mb-2" />
            <h4 className={`text-xs font-bold uppercase ${theme === "dark" ? "text-gray-300" : "text-gray-700"}`}>Referral Streak</h4>
            <p className="text-[10px] text-gray-500 mt-1 leading-tight">
              Invite 1 friend daily to unlock a <span className="font-bold text-purple-500">+10% streak bonus</span>!
            </p>
          </div>
          <div className={`p-4 rounded-2xl ${theme === "dark" ? "bg-gray-800" : "bg-white"} border ${theme === "dark" ? "border-gray-700" : "border-gray-100"}`}>
             <Users className="w-6 h-6 text-blue-500 mb-2" />
             <h4 className={`text-xs font-bold uppercase ${theme === "dark" ? "text-gray-300" : "text-gray-700"}`}>Squad Gifting</h4>
             <p className="text-[10px] text-gray-500 mt-1 leading-tight">
               Every time you invite someone, they get <span className="font-bold text-blue-500">100 ATH</span> to start!
             </p>
          </div>
        </div>

        {/* Invite Code Box */}
        <div className="space-y-3">
          <p className={`text-sm font-bold ml-2 ${theme === "dark" ? "text-blue-400" : "text-blue-600"}`}>
            {getTranslationText("invitation_code")} (Your Username)
          </p>
          <div className={`flex items-center justify-between p-4 rounded-3xl border transition-all ${
            theme === "dark" 
              ? "bg-gray-800 border-gray-700 focus-within:border-blue-500 shadow-xl shadow-blue-500/5" 
              : "bg-white border-gray-200 focus-within:border-blue-400 shadow-xl"
          }`}>
            <span className={`text-2xl font-black tracking-widest ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
              {userData?.user?.username || "---"}
            </span>
            <button 
              onClick={copyInviteLink}
              className={`p-3 rounded-2xl transition-all active:scale-90 ${theme === "dark" ? "bg-blue-600/20 text-blue-400" : "bg-blue-50 text-blue-600"}`}
            >
              <CopyOutline color="currentColor" height="24px" width="24px" />
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 pt-2">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={shareInvite}
            className="flex items-center justify-center gap-3 w-full py-5 px-6 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-3xl font-black text-lg shadow-xl shadow-blue-600/30 transition-all"
          >
            <ShareOutline color="white" height="24px" width="24px" />
            SHARE & EARN ATH + PI
          </motion.button>
          
          <div className="flex items-center gap-3 px-2 py-2">
            <div className={`h-px flex-1 ${theme === "dark" ? "bg-gray-800" : "bg-gray-200"}`}></div>
            <span className={`text-[10px] font-bold uppercase tracking-widest ${theme === "dark" ? "text-gray-600" : "text-gray-400"}`}>
              Your Friends Are Waiting 🤝
            </span>
            <div className={`h-px flex-1 ${theme === "dark" ? "bg-gray-800" : "bg-gray-200"}`}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteUsersPage;

